/**
 * Utility functions for checking workflow step access based on role assignments
 */

/** Message shown when the user has no access to any workflow steps for an application */
export const WORKFLOW_STEP_NO_ACCESS_MESSAGE =
  'You don\'t have access to any workflow steps for this application. Please contact your administrator to have your role assigned to the relevant steps.';

/**
 * Checks if a user has access to a workflow step based on assignedRoleIds
 * 
 * @param {Object} step - The workflow step object
 * @param {string} step.type - The step type (e.g., 'REGISTRATION', 'IN_REVIEW')
 * @param {Array<string>|null|undefined} step.assignedRoleIds - Array of role IDs assigned to this step
 * @param {Array<string>} userRoleIds - Array of role IDs that the current user has
 * @param {boolean} isSuperUser - Whether the user is a superuser (optional)
 * @returns {boolean} - True if user has access, false otherwise
 */
export function hasAccessToStep(step, userRoleIds, isSuperUser = false) {
  if (!step) {
    return false;
  }
  
  // Superuser always has access to all steps
  if (isSuperUser) {
    return true;
  }
  
  // Handle both camelCase and snake_case field names
  const assignedRoleIds = step.assignedRoleIds || step.assigned_role_ids;
  
  // REGISTRATION step is always accessible
  if (step.type === 'REGISTRATION') {
    return true;
  }
  
  // If no role restrictions (null or empty array), step is accessible to all
  if (!assignedRoleIds || assignedRoleIds.length === 0) {
    return true;
  }
  
  // If user has no roles, they don't have access
  if (!userRoleIds || userRoleIds.length === 0) {
    return false;
  }
  
  // Check if user has any matching role
  // Convert both arrays to strings for comparison (handles UUID string comparison)
  const stepRoleIds = assignedRoleIds.map(id => String(id));
  const userRoleIdsStr = userRoleIds.map(id => String(id));
  
  return stepRoleIds.some(roleId => userRoleIdsStr.includes(roleId));
}

/**
 * Checks if a user is a superuser
 * 
 * @param {Object} user - The user object
 * @param {Array} user.adminRoles - Array of admin role objects
 * @returns {boolean} - True if user is a superuser
 */
export function isSuperUser(user) {
  if (!user || !user.adminRoles || !Array.isArray(user.adminRoles)) {
    return false;
  }
  
  // Check if any adminRole has a role with name "Super User" (case-insensitive)
  return user.adminRoles.some((adminRole) => {
    // Try multiple possible paths for role name
    const roleName = adminRole?.roles?.name || 
                     adminRole?.role?.name || 
                     adminRole?.name ||
                     adminRole?.roleName;
    
    if (!roleName) {
      return false;
    }
    
    // Case-insensitive comparison for various superuser role name formats
    const normalizedName = String(roleName).toLowerCase().trim();
    return normalizedName === 'super user' || 
           normalizedName === 'superuser' || 
           normalizedName === 'super_user' ||
           normalizedName === 'super-user' ||
           (normalizedName.includes('super') && normalizedName.includes('user'));
  });
}

/**
 * Extracts role IDs from a user object
 * 
 * @param {Object} user - The user object
 * @param {Array} user.adminRoles - Array of admin role objects
 * @returns {Array<string>} - Array of role IDs
 */
export function getUserRoleIds(user) {
  if (!user || !user.adminRoles || !Array.isArray(user.adminRoles)) {
    return [];
  }
  
  // Extract role IDs from all adminRoles
  return user.adminRoles
    .map((adminRole) => {
      if (adminRole.roles && adminRole.roles.id) {
        return adminRole.roles.id;
      }
      if (adminRole.roleId) {
        return adminRole.roleId;
      }
      return null;
    })
    .filter(id => id != null);
}

/**
 * Checks if a user has access to a candidate's current workflow step
 * 
 * @param {Object} candidate - The candidate object
 * @param {Object} candidate.stage - The current workflow step
 * @param {Array<string>} userRoleIds - Array of role IDs that the current user has
 * @returns {boolean} - True if user has access to candidate's current step
 */
export function hasAccessToCandidateStep(candidate, userRoleIds) {
  if (!candidate || !candidate.stage) {
    return false;
  }
  
  return hasAccessToStep(candidate.stage, userRoleIds);
}

/**
 * Filters workflow steps to only include those the user has access to
 * 
 * @param {Array<Object>} steps - Array of workflow step objects
 * @param {Array<string>} userRoleIds - Array of role IDs that the current user has
 * @param {boolean} isSuperUser - Whether the user is a superuser (optional)
 * @returns {Array<Object>} - Filtered array of accessible workflow steps
 */
export function filterAccessibleSteps(steps, userRoleIds, isSuperUser = false) {
  if (!steps || !Array.isArray(steps)) {
    return [];
  }
  
  return steps.filter(step => hasAccessToStep(step, userRoleIds, isSuperUser));
}
